import React, { useState, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import EmployeeService from '../../services/EmployeeService';
import { addDays, format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select as ShadcnSelect, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Loader2 } from 'lucide-react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import useFetchCustomerRefRecords from '../MyTime/hooks/useFetchCustomerRefs';
import { TextField } from '@material-ui/core';
import { scheduleSchema, workType } from './helper/helper';

const ScheduleFormDialog = ({ open, onOpenChange, schedule, onSave }) => {
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');

  const { customerRefs } = useFetchCustomerRefRecords();

  const isEditing = !!schedule;

  const employeeOptions = useMemo(
    () => employees.map((emp) => ({ value: emp._id, label: emp.DisplayName })),
    [employees]
  );

  const siteOptions = useMemo(() => sites.map((site) => ({ value: site.value, label: site.name })), [sites]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(scheduleSchema),
    defaultValues: {
      date: format(addDays(new Date(), 1), 'dd/MM/yyyy'),
      site: '',
      employees: [],
      startTime: '07:00',
      endTime: '15:00',
      comments: '',
      workType: ''
    }
  });

  const watchedSite = watch('site');
  const watchedWorkType = watch('workType');

  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        setLoading(true);
        try {
          const empRes = await EmployeeService.getActiveEmployees();

          setEmployees(empRes.data.data || []);
          setSites(customerRefs || []);
        } catch (err) {
          setServerError('Failed to load employees or sites');
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [customerRefs, open]);

  useEffect(() => {
    if (open) {
      if (schedule && employees.length > 0) {
        const empId = schedule.employeeId?._id || schedule.employeeId;
        const foundEmp = employeeOptions.find((opt) => opt.value === empId);
        reset({
          date: schedule.date ? format(new Date(schedule.date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
          site: schedule.site?.value || '',
          employees: foundEmp ? [foundEmp] : [],
          startTime: schedule.startTime || '07:00',
          endTime: schedule.endTime || '15:00',
          comments: schedule.comments || '',
          workType: schedule.workType || ''
        });
      } else if (!schedule) {
        reset({
          date: format(new Date(), 'yyyy-MM-dd'),
          site: '',
          employees: [],
          startTime: '07:00',
          endTime: '15:00',
          comments: '',
          workType: ''
        });
      }
      setServerError('');
    }
  }, [open, schedule, employeeOptions, employees.length, reset]);

  const onSubmit = async (data) => {
    setServerError('');
    setSaving(true);
    try {
      // Deduplicate IDs just in case the UI selection added duplicates
      // eslint-disable-next-line no-undef
      const empIds = Array.from(new Set(data.employees.map((opt) => opt.value)));

      await onSave({
        id: schedule?._id,
        employeeIds: isEditing ? undefined : empIds,
        employeeId: isEditing ? empIds[0] : undefined,
        site: data.site
          ? {
              value: data.site,
              name: sites.find((s) => s.value === data.site)?.name
            }
          : null,
        date: data.date,
        startTime: data.startTime,
        endTime: data.endTime,
        comments: data.comments,
        workType: data.workType,
        // eslint-disable-next-line no-undef
        scheduledBy: localStorage.getItem('userId'),
        scheduledDate: new Date().toISOString()
      });
      onOpenChange(false);
    } catch (err) {
      setServerError(err.response?.data?.message || err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tw-sm:max-w-[500px] tw-p-0 tw-overflow-hidden tw-border-none tw-shadow-2xl tw-bg-white">
        <DialogHeader className="tw-p-6 tw-pb-0">
          <DialogTitle className="tw-text-xl tw-font-bold tw-tracking-tight text-dark">
            {isEditing ? 'Edit Schedule' : 'Create New Schedule'}
          </DialogTitle>
        </DialogHeader>

        <div className="tw-p-6">
          {loading ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12 tw-gap-3">
              <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-primary" />
              <p className="tw-text-sm tw-text-muted-foreground">Loading details...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="tw-space-y-4">
              <div className="tw-space-y-4">
                {/* date */}
                <div className="tw-space-y-2">
                  <label className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                    Date
                  </label>
                  <Controller
                    name="date"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        variant="outlined"
                        size="small"
                        type="date"
                        InputLabelProps={{
                          shrink: true
                        }}
                        style={{ width: '100%' }}
                      />
                    )}
                  />
                  {errors.date && <p className="tw-text-xs tw-text-destructive">{errors.date.message}</p>}
                </div>

                {/* sites */}
                <div className="tw-grid tw-grid-cols-1 tw-gap-2">
                  <label className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                    Work Site
                  </label>
                  <Controller
                    name="site"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={siteOptions}
                        placeholder="Select a work site..."
                        className="basic-single"
                        classNamePrefix="select"
                        isClearable
                        value={siteOptions.find((opt) => opt.value === watchedSite) || null}
                        onChange={(opt) => {
                          field.onChange(opt ? opt.value : '');
                          if (opt) setValue('workType', '', { shouldValidate: true });
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            backgroundColor: 'rgba(244, 244, 245, 0.5)',
                            borderColor: 'hsl(var(--muted))'
                          })
                        }}
                      />
                    )}
                  />
                  {errors.site && (
                    <p className="tw-text-xs tw-text-destructive tw-text-red-600">{errors.site.message}</p>
                  )}
                </div>

                <div>
                  <p className="tw-text-center tw-text-xs tw-text-muted-foreground tw-font-bold tw-uppercase">or</p>
                </div>

                {/* or work type */}
                <div className="tw-grid tw-grid-cols-1 tw-gap-2">
                  <label className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                    Leave Category
                  </label>
                  <Controller
                    name="workType"
                    control={control}
                    render={({ field }) => (
                      <ShadcnSelect
                        onValueChange={(val) => {
                          field.onChange(val);
                          if (val) setValue('site', '', { shouldValidate: true });
                        }}
                        value={watchedWorkType}
                      >
                        <SelectTrigger className="tw-w-full tw-bg-muted/50 tw-border-muted hover:tw-bg-muted tw-transition-colors">
                          <SelectValue placeholder="Select a leave category" />
                        </SelectTrigger>
                        <SelectContent className="tw-bg-white">
                          {workType.map((wType) => (
                            <SelectItem key={wType.name} value={wType.name}>
                              {wType.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </ShadcnSelect>
                    )}
                  />
                  {errors.workType && (
                    <p className="tw-text-xs tw-text-destructive tw-text-red-600">{errors.workType.message}</p>
                  )}
                </div>

                {/* employee selection with React Select */}
                <div className="tw-grid tw-grid-cols-1 tw-gap-2">
                  <label className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                    Employees
                  </label>
                  <Controller
                    name="employees"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        isMulti={!isEditing}
                        options={employeeOptions}
                        placeholder="Select employee(s)..."
                        className="basic-multi-select"
                        classNamePrefix="select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            backgroundColor: 'rgba(244, 244, 245, 0.5)',
                            borderColor: 'hsl(var(--muted))'
                          })
                        }}
                      />
                    )}
                  />
                  {errors.employees && <p className="tw-text-xs tw-text-destructive">{errors.employees.message}</p>}

                  {isEditing && (
                    <p className="tw-text-[10px] tw-text-muted-foreground tw-italic">
                      Note: You can only select one employee when editing an existing entry.
                    </p>
                  )}
                </div>

                {/* start and end time */}
                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                  <div className="tw-space-y-2">
                    <label className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                      Start Time
                    </label>
                    <Controller
                      name="startTime"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="time"
                          className="tw-block tw-bg-muted/50 tw-border-muted focus-visible:tw-ring-primary"
                        />
                      )}
                    />
                    {errors.startTime && <p className="tw-text-xs tw-text-destructive">{errors.startTime.message}</p>}
                  </div>
                  <div className="tw-space-y-2 tw-bg-white">
                    <label className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                      End Time
                    </label>
                    <Controller
                      name="endTime"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="time"
                          className="tw-block tw-bg-muted/50 tw-border-muted focus-visible:tw-ring-primary"
                        />
                      )}
                    />
                    {errors.endTime && <p className="tw-text-xs tw-text-destructive">{errors.endTime.message}</p>}
                  </div>
                </div>

                {/* comments */}
                <div className="tw-space-y-2">
                  <label className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                    Comments
                  </label>
                  <Controller
                    name="comments"
                    control={control}
                    render={({ field }) => (
                      <Textarea
                        {...field}
                        placeholder="Optional shift notes..."
                        className="tw-min-h-[80px] tw-bg-muted/50 tw-border-muted focus-visible:tw-ring-primary tw-resize-none"
                      />
                    )}
                  />
                </div>
              </div>

              {serverError && (
                <div className="tw-p-3 tw-rounded-md tw-bg-destructive/10 tw-text-destructive tw-text-sm tw-font-medium">
                  {serverError}
                </div>
              )}

              <div className="tw-flex tw-justify-end tw-gap-3 tw-pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  disabled={saving}
                  className="tw-px-6"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="tw-px-8 tw-bg-primary tw-text-primary-foreground hover:tw-bg-primary/90"
                >
                  {saving ? (
                    <>
                      <Loader2 className="tw-mr-2 tw-h-4 tw-w-4 tw-animate-spin" />
                      Saving...
                    </>
                  ) : isEditing ? (
                    'Update Schedule'
                  ) : (
                    'Create Schedule'
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

ScheduleFormDialog.propTypes = {
  open: PropTypes.any,
  onOpenChange: PropTypes.any,
  onSave: PropTypes.any,
  schedule: PropTypes.any
};

export default ScheduleFormDialog;
